/**
 * WebSocket hook for agent chat sessions.
 * Manages connection lifecycle, streaming accumulation, and history backfill.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { config } from '../../config';
import { fetchSessionHistory } from '../lib/api';
import type { RuntimeEvent, ChatMessage, Message, ServerEvent } from '../lib/agent-types';

type SocketState = {
  messages: ChatMessage[];
  isStreaming: boolean;
  isConnected: boolean;
  error: string | null;
  events: RuntimeEvent[];
};

const INITIAL_STATE: SocketState = {
  messages: [],
  isStreaming: false,
  isConnected: false,
  error: null,
  events: [],
};

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const KNOWN_EVENT_TYPES = new Set(['text', 'tool_use', 'tool_result', 'done', 'error']);

// ── Helpers ─────────────────────────────────────────────────────

/** Derive WebSocket URL from the API URL. */
function buildWsUrl(sessionId: string): string {
  return (
    config.apiUrl.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://') +
    `/api/agents/sessions/${sessionId}`
  );
}

/** Generate a unique message ID. */
function makeId(): string {
  return crypto.randomUUID();
}

/** Calculate reconnect delay with exponential backoff + jitter. */
function reconnectDelay(attempt: number): number {
  const exp = Math.min(BASE_RECONNECT_DELAY * 2 ** attempt, MAX_RECONNECT_DELAY);
  const jitter = Math.random() * exp * 0.5;
  return exp + jitter;
}

/** Extract plain text from a Message's content field. */
function extractText(content: string | { type: string; text?: string }[]): string {
  if (typeof content === 'string') return content;
  return content
    .filter((b) => b.type === 'text' && b.text)
    .map((b) => (b as { text: string }).text)
    .join('');
}

/** Convert history Messages into ChatMessages. */
function historyToChatMessages(messages: Message[]): ChatMessage[] {
  return messages.map((m) => ({
    id: makeId(),
    role: m.role,
    content: extractText(m.content),
    status: 'complete' as const,
    events: [],
  }));
}

// ── Event Processing ────────────────────────────────────────────

/** Apply a RuntimeEvent to the current messages array. */
function applyEvent(messages: ChatMessage[], event: RuntimeEvent): ChatMessage[] {
  switch (event.type) {
    case 'text':
      return applyTextEvent(messages, event.text);
    case 'tool_use':
    case 'tool_result':
      return applyToolEvent(messages, event);
    case 'done':
      return applyDoneEvent(messages, event.text);
    case 'error':
      return finalizeStreaming(messages);
    default:
      return messages;
  }
}

/** Append text to the current streaming assistant message, or create one. */
function applyTextEvent(messages: ChatMessage[], text: string): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role === 'assistant' && last.status === 'streaming') {
    return replaceLastMessage(messages, {
      ...last,
      content: last.content + text,
    });
  }
  return [...messages, newStreamingMessage(text)];
}

/** Add a tool event to the current streaming assistant message. */
function applyToolEvent(messages: ChatMessage[], event: RuntimeEvent): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role === 'assistant' && last.status === 'streaming') {
    return replaceLastMessage(messages, {
      ...last,
      events: [...last.events, event],
    });
  }
  return [...messages, { ...newStreamingMessage(''), events: [event] }];
}

/** Finalize the streaming message with final text from done event. */
function applyDoneEvent(messages: ChatMessage[], finalText: string): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role === 'assistant' && last.status === 'streaming') {
    return replaceLastMessage(messages, {
      ...last,
      content: finalText || last.content,
      status: 'complete',
    });
  }
  return messages;
}

/** Mark any streaming message as complete. */
function finalizeStreaming(messages: ChatMessage[]): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role === 'assistant' && last.status === 'streaming') {
    return replaceLastMessage(messages, { ...last, status: 'complete' });
  }
  return messages;
}

/** Create a new streaming assistant message. */
function newStreamingMessage(text: string): ChatMessage {
  return {
    id: makeId(),
    role: 'assistant',
    content: text,
    status: 'streaming',
    events: [],
  };
}

/** Replace the last element of an array immutably. */
function replaceLastMessage(messages: ChatMessage[], updated: ChatMessage): ChatMessage[] {
  return [...messages.slice(0, -1), updated];
}

// ── Hook ────────────────────────────────────────────────────────

export function useAgentSocket(sessionId: string | null) {
  const [state, setState] = useState<SocketState>(INITIAL_STATE);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingEventsRef = useRef<RuntimeEvent[]>([]);
  const historyLoadedRef = useRef(false);
  const messagesRef = useRef<ChatMessage[]>([]);
  const eventsRef = useRef<RuntimeEvent[]>([]);

  /** Sync messagesRef with state for use in callbacks. */
  const updateMessages = useCallback((updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    setState((prev) => {
      const next = updater(prev.messages);
      messagesRef.current = next;
      return { ...prev, messages: next };
    });
  }, []);

  /** Append a raw event to the events log. */
  const appendEvent = useCallback((event: RuntimeEvent) => {
    eventsRef.current = [...eventsRef.current, event];
    setState((prev) => ({ ...prev, events: eventsRef.current }));
  }, []);

  /** Process a single incoming RuntimeEvent. */
  const processEvent = useCallback(
    (event: RuntimeEvent) => {
      appendEvent(event);
      updateMessages((msgs) => applyEvent(msgs, event));

      if (event.type === 'done' || event.type === 'error') {
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          ...(event.type === 'error' ? { error: (event as { error: string }).error } : {}),
        }));
      } else if (event.type === 'text' || event.type === 'tool_use') {
        setState((prev) => ({ ...prev, isStreaming: true }));
      }
    },
    [appendEvent, updateMessages]
  );

  /** Replay buffered events after history is loaded. */
  const replayPendingEvents = useCallback(() => {
    const pending = pendingEventsRef.current;
    pendingEventsRef.current = [];
    for (const event of pending) {
      processEvent(event);
    }
  }, [processEvent]);

  /** Handle an incoming WebSocket message. */
  const handleMessage = useCallback(
    (data: string) => {
      let parsed: ServerEvent;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }

      // Ignore non-RuntimeEvent messages (e.g. queue_position, cf_agent_mcp_servers)
      if (!KNOWN_EVENT_TYPES.has(parsed.type)) return;

      const event = parsed as RuntimeEvent;
      if (!historyLoadedRef.current) {
        pendingEventsRef.current.push(event);
      } else {
        processEvent(event);
      }
    },
    [processEvent]
  );

  /** Open a WebSocket connection to the session. */
  const connectRef = useRef<(sid: string) => void>(() => {});

  /** Schedule a reconnect with exponential backoff. */
  const scheduleReconnect = useCallback((sid: string, code: number) => {
    // Don't reconnect on normal close
    if (code === 1000) return;

    const delay = reconnectDelay(reconnectAttemptRef.current);
    reconnectAttemptRef.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectRef.current(sid);
    }, delay);
  }, []);

  const connect = useCallback(
    (sid: string) => {
      const ws = new WebSocket(buildWsUrl(sid));
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setState((prev) => ({ ...prev, isConnected: true, error: null }));
      };

      ws.onmessage = (e) => handleMessage(e.data as string);

      ws.onclose = (e) => {
        wsRef.current = null;
        setState((prev) => ({ ...prev, isConnected: false }));
        scheduleReconnect(sid, e.code);
      };

      ws.onerror = () => {
        // onclose will fire after onerror, so we handle reconnect there
      };
    },
    [handleMessage, scheduleReconnect]
  );

  // Keep connectRef in sync for scheduleReconnect to use
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  /** Cleanup WebSocket and timers. */
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent reconnect on intentional close
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  /** Send a message to the agent. */
  const send = useCallback(
    (input: string) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

      const userMessage: ChatMessage = {
        id: makeId(),
        role: 'user',
        content: input,
        status: 'complete',
        events: [],
      };

      updateMessages((msgs) => [...msgs, userMessage]);
      wsRef.current.send(JSON.stringify({ type: 'send', input }));
    },
    [updateMessages]
  );

  /** Reset all state and refs to initial values. */
  const reset = useCallback(() => {
    cleanup();
    messagesRef.current = [];
    eventsRef.current = [];
    pendingEventsRef.current = [];
    historyLoadedRef.current = false;
    reconnectAttemptRef.current = 0;
  }, [cleanup]);

  // ── Lifecycle Effect ────────────────────────────────────────────

  useEffect(() => {
    reset();

    if (!sessionId) return;

    // Connect first, then fetch history
    connect(sessionId);

    // Load history asynchronously to avoid synchronous setState in effect
    let cancelled = false;
    fetchSessionHistory(sessionId)
      .then(({ messages }) => {
        if (cancelled) return;
        const chatMessages = historyToChatMessages(messages);
        messagesRef.current = chatMessages;
        setState((prev) => ({ ...prev, messages: chatMessages }));
        historyLoadedRef.current = true;
        replayPendingEvents();
      })
      .catch(() => {
        if (cancelled) return;
        historyLoadedRef.current = true;
        replayPendingEvents();
      });

    return () => {
      cancelled = true;
      reset();
    };
  }, [sessionId, connect, reset, replayPendingEvents]);

  return {
    messages: state.messages,
    isStreaming: state.isStreaming,
    isConnected: state.isConnected,
    error: state.error,
    events: state.events,
    send,
  };
}
